import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import LinearProgress from "@mui/material/LinearProgress";
import {
  exportCashFlowReportToExcel,
  exportCashFlowReportToPDF,
} from "../../utils/exportCashFlowReport";

const money = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
};

const CATEGORY_LABELS = {
  opening_balance: "Opening Balance",
  inflow: "Inflow",
  outflow: "Outflow",
  net: "Net",
  closing_balance: "Closing Balance",
  invoice_payments: "Invoice Payments",
  purchase_payments: "Purchase Payments",
  transfer_in: "Transfer In",
  transfer_out: "Transfer Out",
  expenses: "Expenses",
  payroll: "Payroll",
  advance: "Advance",
  credit_total: "Credit Total",
  debit_total: "Debit Total",
  other_inflow: "Other Inflow",
  other_outflow: "Other Outflow",
};

const METRIC_COLORS = {
  opening_balance: { accent: "#1d4ed8", bg: "#eff6ff", text: "#1e3a8a" },
  closing_balance: { accent: "#0f766e", bg: "#ecfeff", text: "#115e59" },
  inflow: { accent: "#15803d", bg: "#f0fdf4", text: "#166534" },
  invoice_payments: { accent: "#15803d", bg: "#f0fdf4", text: "#166534" },
  transfer_in: { accent: "#b45309", bg: "#fffbeb", text: "#92400e" },
  other_inflow: { accent: "#0369a1", bg: "#f0f9ff", text: "#075985" },
  outflow: { accent: "#b91c1c", bg: "#fef2f2", text: "#991b1b" },
  purchase_payments: { accent: "#b91c1c", bg: "#fef2f2", text: "#991b1b" },
  expenses: { accent: "#b91c1c", bg: "#fef2f2", text: "#991b1b" },
  payroll: { accent: "#7c2d12", bg: "#fff7ed", text: "#9a3412" },
  advance: { accent: "#7c2d12", bg: "#fff7ed", text: "#9a3412" },
  transfer_out: { accent: "#b45309", bg: "#fffbeb", text: "#92400e" },
  other_outflow: { accent: "#7f1d1d", bg: "#fef2f2", text: "#7f1d1d" },
};

const getMetricStyle = (key, value) => {
  if (key === "net") {
    const amount = Number(value);
    if (amount > 0) return { accent: "#15803d", bg: "#f0fdf4", text: "#166534" };
    if (amount < 0) return { accent: "#b91c1c", bg: "#fef2f2", text: "#991b1b" };
    return { accent: "#475569", bg: "#f8fafc", text: "#334155" };
  }
  return METRIC_COLORS[key] || { accent: "#475569", bg: "#f8fafc", text: "#334155" };
};

const getCategoryChipStyle = (category) => {
  const style = getMetricStyle(category, 0);
  return {
    backgroundColor: style.bg,
    color: style.text,
    borderColor: style.accent,
  };
};

const getTypeChipStyle = (type) =>
  type === "credit"
    ? { backgroundColor: "#f0fdf4", color: "#166534", borderColor: "#15803d" }
    : { backgroundColor: "#fef2f2", color: "#991b1b", borderColor: "#b91c1c" };

const isSystemClearingAccount = (name) => {
  const text = String(name || "").trim().toLowerCase();
  return (
    (text.startsWith("purchase ") && text.endsWith(" clearing")) ||
    (text.startsWith("sale ") && text.endsWith(" clearing"))
  );
};

const CashFlowReport = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const selectedBranchId = auth?.user?.selected_branch
    ? String(auth.user.selected_branch)
    : "";

  const [shopId, setShopId] = useState(selectedBranchId);
  const [accountId, setAccountId] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [search, setSearch] = useState("");

  const { data: shopsData } = useFetchObjects(
    ["cash-flow-shops"],
    "shop/shop/?page_size=all",
    token
  );
  const { data: accountsData } = useFetchObjects(
    ["cash-flow-accounts", shopId],
    shopId ? `shop/accounts/?page_size=all&shop=${shopId}` : "shop/accounts/?page_size=all",
    token
  );

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (shopId) params.append("shop", shopId);
    if (accountId) params.append("account", accountId);
    if (startDate) params.append("start_date", startDate.format("YYYY-MM-DD"));
    if (endDate) params.append("end_date", endDate.format("YYYY-MM-DD"));
    if (search.trim()) params.append("search", search.trim());
    return params.toString();
  }, [shopId, accountId, startDate, endDate, search]);

  const {
    data: reportData,
    isLoading,
    isError,
    refetch,
  } = useFetchObjects(
    ["cash-flow-report", queryString],
    `shop/cash-flow-report/?${queryString}`,
    token
  );

  const shops = useMemo(
    () => (Array.isArray(shopsData) ? shopsData : shopsData?.results || []),
    [shopsData]
  );
  const accounts = useMemo(
    () =>
      (Array.isArray(accountsData) ? accountsData : accountsData?.results || []).filter(
        (account) => !isSystemClearingAccount(account.name)
      ),
    [accountsData]
  );
  const summary = useMemo(() => reportData?.summary || [], [reportData]);
  const accountRows = useMemo(() => reportData?.accounts || [], [reportData]);
  const dailyRows = useMemo(() => reportData?.daily || [], [reportData]);
  const records = useMemo(() => reportData?.records || [], [reportData]);

  const summaryMetricKeys = [
    "opening_balance",
    "inflow",
    "outflow",
    "net",
    "closing_balance",
    "invoice_payments",
    "purchase_payments",
    "transfer_in",
    "transfer_out",
    "expenses",
    "payroll",
    "advance",
    "other_inflow",
    "other_outflow",
  ];

  const handleReset = () => {
    setShopId(selectedBranchId);
    setAccountId("");
    setStartDate(null);
    setEndDate(null);
    setSearch("");
  };

  const exportPayload = useMemo(
    () => ({
      filters: reportData?.filters || {},
      summary,
      accounts: accountRows,
      daily: dailyRows,
      records,
    }),
    [reportData, summary, accountRows, dailyRows, records]
  );

  const handleExportPDF = () => {
    exportCashFlowReportToPDF(exportPayload);
  };

  const handleExportExcel = () => {
    exportCashFlowReportToExcel(exportPayload);
  };

  if (isLoading) {
    return (
      <Box sx={{ height: "100vh", display: "flex", justifyContent: "center", width: "100%" }}>
        <LinearProgress sx={{ width: "90%" }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{t("ReportIndex.error", "Error loading report")}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={2.2}>
            <FormControl fullWidth>
              <Select value={shopId} onChange={(e) => { setShopId(e.target.value); setAccountId(""); }} displayEmpty>
                <MenuItem value="">{t("reports.allBranches", "All Branches")}</MenuItem>
                {shops.map((shop) => (
                  <MenuItem key={shop.id} value={String(shop.id)}>
                    {shop.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.2}>
            <FormControl fullWidth>
              <Select value={accountId} onChange={(e) => setAccountId(e.target.value)} displayEmpty>
                <MenuItem value="">{t("reports.allAccounts", "All Accounts")}</MenuItem>
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={String(account.id)}>
                    {account.name} ({account.currency_title})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1.8}>
            <DatePicker
              label={t("ReportIndex.datepicker_start", "Start date")}
              value={startDate}
              onChange={setStartDate}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} md={1.8}>
            <DatePicker
              label={t("ReportIndex.datepicker_end", "End date")}
              value={endDate}
              onChange={setEndDate}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              label={t("reports.cashFlow_search", "Search")}
              placeholder={t("reports.cashFlow_searchPlaceholder", "Search description, account...")}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button variant="outlined" onClick={handleReset}>
                {t("ReportIndex.btn_clear", "Clear")}
              </Button>
              <Button variant="outlined" onClick={refetch}>
                {t("reports.refresh", "Refresh")}
              </Button>
              <Button variant="contained" onClick={handleExportPDF}>
                {t("ReportIndex.export_pdf", "Export PDF")}
              </Button>
              <Button variant="contained" color="success" onClick={handleExportExcel}>
                {t("stock.exportExcel", "Export Excel")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </LocalizationProvider>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5">Cash Flow Report</Typography>
        <Typography variant="body2" color="text.secondary">
          Date range: {reportData?.filters?.start_date || "--"} to {reportData?.filters?.end_date || "--"}
        </Typography>
      </Box>

      {summary.map((currencySummary) => (
        <Box key={currencySummary.currency} sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Totals - {currencySummary.currency}
          </Typography>
          <Grid container spacing={2}>
            {summaryMetricKeys.map((key) => (
              <Grid item xs={12} sm={6} md={3} key={`${currencySummary.currency}-${key}`}>
                <Card
                  sx={{
                    p: 2,
                    minHeight: 110,
                    backgroundColor: getMetricStyle(key, currencySummary[key]).bg,
                    borderLeft: `5px solid ${getMetricStyle(key, currencySummary[key]).accent}`,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: getMetricStyle(key, currencySummary[key]).text }}>
                    {CATEGORY_LABELS[key]}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: getMetricStyle(key, currencySummary[key]).text }}
                  >
                    {money(currencySummary[key])} {currencySummary.currency}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell colSpan={10} align="center">
                <Typography variant="h6">Account Summary</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Account</TableCell>
              <TableCell>Branch</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>Opening</TableCell>
              <TableCell>Inflow</TableCell>
              <TableCell>Outflow</TableCell>
              <TableCell>Transfer In</TableCell>
              <TableCell>Transfer Out</TableCell>
              <TableCell>Net</TableCell>
              <TableCell>Closing</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accountRows.map((row) => (
              <TableRow key={row.account_id}>
              <TableCell>{row.account_name}</TableCell>
              <TableCell>{row.shop_name}</TableCell>
              <TableCell>{row.currency}</TableCell>
              <TableCell sx={{ color: getMetricStyle("opening_balance", row.opening_balance).text, fontWeight: 600 }}>{money(row.opening_balance)}</TableCell>
              <TableCell sx={{ color: getMetricStyle("inflow", row.inflow).text, fontWeight: 600 }}>{money(row.inflow)}</TableCell>
              <TableCell sx={{ color: getMetricStyle("outflow", row.outflow).text, fontWeight: 600 }}>{money(row.outflow)}</TableCell>
              <TableCell sx={{ color: getMetricStyle("transfer_in", row.transfer_in).text, fontWeight: 600 }}>{money(row.transfer_in)}</TableCell>
              <TableCell sx={{ color: getMetricStyle("transfer_out", row.transfer_out).text, fontWeight: 600 }}>{money(row.transfer_out)}</TableCell>
              <TableCell sx={{ color: getMetricStyle("net", row.net).text, fontWeight: 700 }}>{money(row.net)}</TableCell>
              <TableCell sx={{ color: getMetricStyle("closing_balance", row.closing_balance).text, fontWeight: 700 }}>{money(row.closing_balance)}</TableCell>
            </TableRow>
          ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Typography variant="h6">Daily Cash Flow</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>Inflow</TableCell>
              <TableCell>Outflow</TableCell>
              <TableCell>Transfer In</TableCell>
              <TableCell>Transfer Out</TableCell>
              <TableCell>Net</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dailyRows.map((row) => (
              <TableRow key={`${row.date}-${row.currency}`}>
                <TableCell>{row.date}</TableCell>
                <TableCell>{row.currency}</TableCell>
                <TableCell sx={{ color: getMetricStyle("inflow", row.inflow).text, fontWeight: 600 }}>{money(row.inflow)}</TableCell>
                <TableCell sx={{ color: getMetricStyle("outflow", row.outflow).text, fontWeight: 600 }}>{money(row.outflow)}</TableCell>
                <TableCell sx={{ color: getMetricStyle("transfer_in", row.transfer_in).text, fontWeight: 600 }}>{money(row.transfer_in)}</TableCell>
                <TableCell sx={{ color: getMetricStyle("transfer_out", row.transfer_out).text, fontWeight: 600 }}>{money(row.transfer_out)}</TableCell>
                <TableCell sx={{ color: getMetricStyle("net", row.net).text, fontWeight: 700 }}>{money(row.net)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell colSpan={10} align="center">
                <Typography variant="h6">Full Records</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Branch</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Txn Currency</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Account Currency</TableCell>
              <TableCell>Converted</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.transaction_date}</TableCell>
                <TableCell>{row.shop_name}</TableCell>
                <TableCell>{row.account_name}</TableCell>
                <TableCell>
                  <Chip
                    label={CATEGORY_LABELS[row.category] || row.category}
                    size="small"
                    variant="outlined"
                    sx={getCategoryChipStyle(row.category)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.transaction_type}
                    size="small"
                    variant="outlined"
                    sx={getTypeChipStyle(row.transaction_type)}
                  />
                </TableCell>
                <TableCell>{row.transaction_currency}</TableCell>
                <TableCell sx={{ color: row.transaction_type === "credit" ? getMetricStyle("inflow", row.amount).text : getMetricStyle("outflow", row.amount).text, fontWeight: 600 }}>
                  {money(row.amount)}
                </TableCell>
                <TableCell>{row.account_currency}</TableCell>
                <TableCell sx={{ color: row.transaction_type === "credit" ? getMetricStyle("inflow", row.converted_amount).text : getMetricStyle("outflow", row.converted_amount).text, fontWeight: 700 }}>
                  {money(row.converted_amount)}
                </TableCell>
                <TableCell>{row.description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CashFlowReport;
